import { zodResolver } from '@hookform/resolvers/zod';
import { forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { popModal } from '.';

const confirmReservationWithTagSchema = z.object({
  tag: z
    .string()
    .max(10, { message: 'form.errors.tag-max-length' })
    .optional()
    .transform((val) => val?.trim() || undefined),
});

type ConfirmReservationWithTagForm = z.infer<
  typeof confirmReservationWithTagSchema
>;

export type ConfirmReservationWithTagModalApi = {
  onConfirm: (data: { tag?: string }) => void;
  onClose?: () => void;
  onCancel?: () => void;
};

export type ConfirmReservationWithTagModalProps = {
  show?: boolean;
  doctorSubstituteName: string;
} & ConfirmReservationWithTagModalApi;

export const ConfirmReservationWithTagModal = forwardRef(
  (
    {
      show = true,
      doctorSubstituteName,
      onConfirm,
      onClose,
      onCancel,
    }: ConfirmReservationWithTagModalProps,
    ref,
  ) => {
    const { t } = useTranslation();

    const form = useForm<ConfirmReservationWithTagForm>({
      resolver: zodResolver(confirmReservationWithTagSchema),
      defaultValues: {
        tag: '',
      },
    });

    const handleClose = () => {
      popModal('ConfirmReservationWithTagModal');
      onClose?.();
    };

    const handleCancel = () => {
      popModal('ConfirmReservationWithTagModal');
      onCancel?.();
    };

    const handleConfirm = (data: ConfirmReservationWithTagForm) => {
      onConfirm({ tag: data.tag });
      popModal('ConfirmReservationWithTagModal');
    };

    useImperativeHandle(ref, () => ({
      close: handleClose,
    }));

    return (
      <Dialog
        open={show}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('confirm-reservation-with-tag-modal.title')}
            </DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="confirm-reservation-with-tag-modal.description"
                values={{ doctorName: doctorSubstituteName }}
                components={{ strong: <strong />, br: <br /> }}
              />
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleConfirm)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="tag"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('view-admin-agenda-slot-sheet.reservation.tag.label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t(
                          'view-admin-agenda-slot-sheet.reservation.tag.placeholder',
                        )}
                        maxLength={10}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                >
                  {t('btn.cancel')}
                </Button>

                <Button
                  type="submit"
                  variant="default"
                >
                  {t('btn.confirm')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  },
);
